import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";

import { AvatarProfile, Chip, MultipleSelectInput } from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";

import { EmployeeValue, createFetchEmployeesForPicker } from "./shared";

export type MultipleSelectEmployeeProps = {
	value: EmployeeValue[];
	onChange: (employees: EmployeeValue[]) => void;
	fetchEmployees?: (searchTerm: string) => Promise<{ id: string; name: string; label?: string }[]>;
	/** Ignored when `fetchEmployees` is provided; only applies to the default fetcher. */
	excludeCurrentUser?: boolean;
	errored?: boolean;
	disabled?: boolean;
	placeholder?: string;
	id?: string;
	"data-testid"?: string;
	dangerouslySetClassName?: string;
	maxSelections?: number;
	ariaLabel?: string;
};

export const Multiple = ({
	value,
	onChange,
	fetchEmployees: customFetchEmployees,
	excludeCurrentUser,
	errored,
	disabled,
	placeholder = "Search for employees...",
	id,
	"data-testid": dataTestId,
	dangerouslySetClassName,
	maxSelections,
	ariaLabel,
}: MultipleSelectEmployeeProps) => {
	const queryClient = useQueryClient();

	const fetchEmployeeOptions = useCallback(
		async (searchTerm: string): Promise<Option<EmployeeValue>[]> => {
			try {
				let employees: { id: string; name: string; label?: string }[];

				if (customFetchEmployees) {
					employees = await customFetchEmployees(searchTerm);
				} else {
					const defaultFetch = createFetchEmployeesForPicker(queryClient, { excludeCurrentUser });
					employees = await defaultFetch(searchTerm);
				}

				return employees.map((employee) => ({
					label: employee.label ?? employee.name,
					value: {
						id: employee.id,
						name: employee.name,
					},
				}));
			} catch (error) {
				console.error("Error fetching employees:", error);
				return [];
			}
		},
		[queryClient, customFetchEmployees, excludeCurrentUser]
	);

	const renderEmployeeChip = useCallback(
		(value: EmployeeValue, onDismiss?: () => void) => (
			<Chip
				employeeId={value.id}
				employeeName={value.name}
				color="neutral"
				size={20}
				onDismiss={onDismiss}
				dangerouslySetClassName="flex items-center"
			/>
		),
		[]
	);

	const renderEmployeeOption = useCallback(
		(option: Option<EmployeeValue>) => (
			<AvatarProfile
				employeeId={option.value.id}
				employeeName={option.value.name}
				avatarSize={20}
				textSize="paragraph-3"
				noLink
			/>
		),
		[]
	);

	return (
		<MultipleSelectInput<EmployeeValue>
			fetchOptions={fetchEmployeeOptions}
			value={value}
			onChange={onChange}
			selectionVariant="combobox"
			placeholder={placeholder}
			errored={errored}
			disabled={disabled}
			id={id}
			data-testid={dataTestId}
			renderValue={renderEmployeeChip}
			renderOption={renderEmployeeOption}
			dangerouslySetClassName={dangerouslySetClassName}
			maxSelections={maxSelections}
			ariaLabel={ariaLabel}
		/>
	);
};

Multiple.displayName = "MultipleSelectEmployee";
